import React, { ReactNode, useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import styles from "./Button.module.scss";

interface DropdownOption {
  label?: string;
}

interface ButtonV2Props {
  type?: "primary" | "secondary" | "tertiary";
  size?: "small" | "medium" | "large";
  mode?: "default" | "inverted";
  variant?: "" | "IconButton" | "splitButton";

  children?: ReactNode;
  iconBefore?: ReactNode;
  iconAfter?: ReactNode;
  dropdownIcon?: ReactNode;

  onClick?: () => void;
  onSelect?: (item: DropdownOption | string) => void;

  dropdownOptions?: Array<DropdownOption | string>;

  disabled?: boolean;
  className?: string;
}

export function Button_V2({
  type = "primary",
  size = "medium",
  children = "Button",
  iconBefore = null,
  iconAfter = null,
  onClick,
  onSelect,
  dropdownOptions = [],
  disabled = false,
  className = "",
  variant = "",
  mode = "default",
  dropdownIcon = "▾",
}: ButtonV2Props) {
  const [open, setOpen] = useState(false);

  const buttonClasses = clsx(
    styles.button,
    styles[size],
    styles[mode],
    styles[type],
    variant === "IconButton" && styles.iconButton,
    disabled && styles.disabled,
    className
  );

  if (variant === "splitButton") {
    return (
      <div className={styles.splitWrapper}>
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen(!open)}
          className={buttonClasses}
        >
          <span>{children}</span>
          <span className={styles.divider}></span>
          <span>{dropdownIcon}</span>
        </button>

        {open && (
          <div className={styles.dropdown}>
            {dropdownOptions.map((item, index) => (
              <button
                key={index}
                type="button"
                className={styles.dropdownItem}
                onClick={() => {
                  setOpen(false);
                  onSelect?.(item);
                }}
              >
                {
                  typeof item === "object" && item !== null
                    ? item.label ?? ""
                    : item
                }
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={buttonClasses}
    >
      {iconBefore && (
        <span className={styles.icon}>
          {iconBefore}
        </span>
      )}

      <span>{children}</span>

      {iconAfter && (
        <span className={styles.icon}>
          {iconAfter}
        </span>
      )}
    </button>
  );
}

Button_V2.propTypes = {
  type: PropTypes.oneOf(["primary", "secondary", "tertiary"]),
  size: PropTypes.oneOf(["small", "medium", "large"]),
  mode: PropTypes.oneOf(["default", "inverted"]),
  variant: PropTypes.oneOf(["", "IconButton", "splitButton"]),
  children: PropTypes.node,
  iconBefore: PropTypes.node,
  iconAfter: PropTypes.node,
  dropdownIcon: PropTypes.node,
  onClick: PropTypes.func,
  onSelect: PropTypes.func,
  dropdownOptions: PropTypes.array,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};

export default Button_V2;